import React from "react";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

interface UserPrivilege {
  manage_users: boolean;
  all_patients?: boolean;
  questionnaires?: boolean;
  email_and_sms_templates?: boolean;
  config?: boolean;
  file_types?: boolean;
  task_types?: boolean;
  wellness?: boolean;
  prompt_development?: boolean;
  doc_assistant?: boolean;
  clinic_login?: boolean;
  hmo?: boolean;
  referral?: boolean;
  leads?: boolean;
  view_video_rooms?: boolean;
}

interface User {
  tenant_id: number | null;
  role?: string;
  user_privilege?: UserPrivilege | null;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  showOnlyWhen?: (activeItem: string) => boolean;
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  activeItem?: string;
  onNavigate?: (path: string) => void;
  user?: User | null;
  currentPath?: string;
  configSyncAvailable?: boolean;
  taskCount?: number;
}

const menuItems: MenuItem[] = [
  { id: "calendar", label: "Calendar", icon: "📅", path: "/calendar" },
  { id: "questionnaires", label: "Questionnaires", icon: "📝", path: "/questionnaires" },
  { id: "patients", label: "Patients", icon: "👥", path: "/patients" },
  { id: "patient-page", label: "Patient Page", icon: "📄", path: "", showOnlyWhen: (activeItem) => activeItem === "patient-page" },
  { id: "leads", label: "Leads", icon: "📋", path: "/leads" },
  { id: "video-rooms", label: "Video Rooms", icon: "📹", path: "/video-rooms" },
  { id: "tasks", label: "Tasks", icon: "⚠️", path: "/tasks" },
  { id: "internal-tasks", label: "Internal Tasks", icon: "📋", path: "/internal-tasks" },
  { id: "sms-templates", label: "SMS Templates", icon: "💬", path: "/sms-templates" },
  { id: "email-templates", label: "Email Templates", icon: "✉️", path: "/email-templates" },
  { id: "users", label: "Users", icon: "👤", path: "/users" },
  { id: "user-privileges", label: "User Privileges", icon: "🔐", path: "/user-privileges" },
  { id: "tenants", label: "Tenants", icon: "🏢", path: "/tenants" },
  { id: "clinics", label: "Clinics", icon: "🏥", path: "/clinics" },
  { id: "services", label: "Services", icon: "🧭", path: "/services" },
  { id: "referrals", label: "Referrals", icon: "🤝", path: "/referrals" },
  { id: "meeting-types", label: "Meeting Types", icon: "📂", path: "/meeting-types" },
  { id: "summary-types", label: "Summary Types", icon: "📄", path: "/summary-types" },
  { id: "internal-task-types", label: "Task Types", icon: "🗂️", path: "/internal-task-types" },
  { id: "file-types", label: "File Types", icon: "📁", path: "/file-types" },
  { id: "patient-status-configs", label: "Patient Statuses", icon: "🏷️", path: "/patient-status-configs" },
  { id: "patient-types", label: "Patient Types", icon: "🏷️", path: "/patient-types" },
  { id: "patient-field-configs", label: "Patient Fields", icon: "📝", path: "/patient-field-configs" },
  { id: "lead-status-configs", label: "Lead Statuses", icon: "🏷️", path: "/lead-status-configs" },
  { id: "lead-field-configs", label: "Lead Fields", icon: "🗂️", path: "/lead-field-configs" },
  { id: "questionnaire-types", label: "Questionnaire Builder", icon: "📋", path: "/questionnaire-types" },
  { id: "activity-logs", label: "Activity Logs", icon: "📜", path: "/activity-logs" },
  { id: "unmatched-communications", label: "Unmatched Comms", icon: "📨", path: "/unmatched-communications" },
  { id: "config-sync", label: "Config Sync", icon: "🔄", path: "/config-sync" },
  { id: "doc-assistant", label: "Doc Assistant", icon: "📑", path: "/doc-assistant" },
  { id: "patient-notifications", label: "Notifications", icon: "🔔", path: "/patient-notifications" },
  { id: "task-rules", label: "Task Rules", icon: "📋", path: "/task-rules" },
  { id: "medications", label: "Medications", icon: "💊", path: "/medications" },
  { id: "prompts", label: "Prompts", icon: "🤖", path: "/prompts" },
  { id: "wellness-configs", label: "Wellness Config", icon: "🌿", path: "/wellness-configs" },
  { id: "wellness-items", label: "Wellness Items", icon: "✨", path: "/wellness-items" },
  { id: "wellness-tips", label: "Wellness Tips", icon: "💡", path: "/wellness-tips" },
  { id: "diagnosis-types", label: "Diagnosis Types", icon: "🩺", path: "/diagnosis-types" },
];

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  activeItem = "dashboard",
  onNavigate,
  user,
  currentPath = "",
  configSyncAvailable = false,
  taskCount = 0,
}) => {
  const STORAGE_KEY = 'sidebar_active_tab';
  const [activeTab, setActiveTab] = React.useState<'main' | 'config' | 'wellness'>(
    () => (localStorage.getItem(STORAGE_KEY) as 'main' | 'config' | 'wellness') || 'main'
  );

  const handleTabChange = (tab: 'main' | 'config' | 'wellness') => {
    setActiveTab(tab);
    localStorage.setItem(STORAGE_KEY, tab);
  };

  const hasConfigPrivilege = user?.user_privilege?.config || user?.user_privilege?.email_and_sms_templates || user?.user_privilege?.prompt_development || user?.user_privilege?.doc_assistant || user?.user_privilege?.referral || user?.user_privilege?.file_types || user?.user_privilege?.task_types || configSyncAvailable || false;
  const hasWellnessPrivilege = user?.user_privilege?.wellness || false;

  const configItems = ['clinics', 'services', 'referrals', 'meeting-types', 'summary-types', 'internal-task-types', 'file-types', 'patient-status-configs', 'patient-types', 'patient-field-configs', 'lead-status-configs', 'lead-field-configs', 'sms-templates', 'email-templates', 'activity-logs', 'unmatched-communications', 'patient-notifications', 'task-rules', 'medications', 'prompts', 'config-sync', 'doc-assistant'];
  const wellnessItems = ['wellness-configs', 'wellness-items', 'wellness-tips', 'diagnosis-types'];

  // Build available tab options
  const tabOptions: { value: 'main' | 'config' | 'wellness'; label: string }[] = [
    { value: 'main', label: 'Main' },
    ...(hasConfigPrivilege ? [{ value: 'config' as const, label: 'Config' }] : []),
    ...(hasWellnessPrivilege ? [{ value: 'wellness' as const, label: 'Wellness' }] : []),
  ];

  const hasMultipleTabs = tabOptions.length > 1;

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.showOnlyWhen && !item.showOnlyWhen(activeItem || "")) return false;
    if (item.id === "tenants" && user?.tenant_id !== 0) return false;
    if ((item.id === "users" || item.id === "user-privileges") && !user?.user_privilege?.manage_users) return false;
    if (item.id === "leads" && !user?.user_privilege?.leads) return false;
    if (item.id === "video-rooms" && !user?.user_privilege?.view_video_rooms) return false;
    if (item.id === "questionnaires" && !user?.user_privilege?.all_patients) return false;
    if (item.id === "questionnaire-types" && !user?.user_privilege?.questionnaires) return false;
    if ((item.id === "sms-templates" || item.id === "email-templates") && !user?.user_privilege?.email_and_sms_templates) return false;
    if (item.id === "file-types" && !user?.user_privilege?.file_types) return false;
    if (item.id === "internal-task-types" && !user?.user_privilege?.task_types) return false;
    if ((item.id === "clinics" || item.id === "services" || item.id === "meeting-types" || item.id === "summary-types" || item.id === "patient-status-configs" || item.id === "patient-types" || item.id === "patient-field-configs" || item.id === "lead-status-configs" || item.id === "lead-field-configs" || item.id === "activity-logs" || item.id === "unmatched-communications" || item.id === "patient-notifications" || item.id === "task-rules" || item.id === "medications") && !user?.user_privilege?.config) return false;
    if (item.id === "prompts" && !user?.user_privilege?.prompt_development) return false;
    if (item.id === "doc-assistant" && !user?.user_privilege?.doc_assistant) return false;
    if (item.id === "referrals" && !user?.user_privilege?.referral) return false;
    // Patients is visible to everyone; the list is filtered server-side — users
    // without all_patients only see patients they are on the care team of.
    if ((item.id === "wellness-configs" || item.id === "wellness-items" || item.id === "wellness-tips" || item.id === "diagnosis-types") && !hasWellnessPrivilege) return false;
    if (item.id === "config-sync" && !configSyncAvailable) return false;
    return true;
  });

  const displayedMenuItems = hasMultipleTabs
    ? visibleMenuItems.filter((item) => {
        if (wellnessItems.includes(item.id)) return activeTab === 'wellness';
        if (configItems.includes(item.id)) return activeTab === 'config';
        return activeTab === 'main';
      })
    : visibleMenuItems;

  return (
    <div
      style={{
        width: isCollapsed ? 60 : 200,
        backgroundColor: "#3A7380",
        height: "calc(100vh - 60px)",
        position: "fixed",
        left: 0,
        top: 60,
        transition: "width 0.3s ease",
        display: "flex",
        flexDirection: "column",
        zIndex: 900,
        overflow: "visible",
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        style={{
          position: "absolute",
          right: -16,
          top: 20,
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#3A7380",
          border: "2px solid #fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          zIndex: 1,
          padding: 0,
          transition: "all 0.3s ease",
        }}
      >
        {isCollapsed ? <KeyboardArrowRight sx={{ fontSize: 20 }} /> : <KeyboardArrowLeft sx={{ fontSize: 20 }} />}
      </button>

      {/* Scrollable content area */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
      {/* Tab Dropdown (only show if multiple tabs available and sidebar not collapsed) */}
      {hasMultipleTabs && !isCollapsed && (
        <div style={{ padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <select
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value as 'main' | 'config' | 'wellness')}
            style={{
              width: '100%',
              padding: '8px 10px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              paddingRight: '28px',
            }}
          >
            {tabOptions.map((opt) => (
              <option key={opt.value} value={opt.value} style={{ backgroundColor: '#3A7380', color: '#fff' }}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Menu Items */}
      <nav style={{ marginTop: hasMultipleTabs && !isCollapsed ? 12 : 20 }}>
        {displayedMenuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => {
              if (item.id !== "patient-page" && item.path) {
                onNavigate?.(item.path);
              }
            }}
            style={{
              width: "100%",
              padding: isCollapsed ? "16px 0" : (item.id === "patient-page" ? "16px 20px 16px 40px" : "16px 20px"),
              backgroundColor: activeItem === item.id ? "rgba(255,255,255,0.1)" : "transparent",
              border: "none",
              borderLeft: activeItem === item.id ? "4px solid #fff" : "4px solid transparent",
              color: "#fff",
              cursor: item.id === "patient-page" ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 14,
              fontWeight: 500,
              transition: "all 0.2s",
              justifyContent: isCollapsed ? "center" : "flex-start",
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              if (activeItem !== item.id && item.id !== "patient-page") {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (activeItem !== item.id) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <span style={{ position: "relative", fontSize: 18, display: "inline-flex" }}>
              {item.icon}
              {item.id === "internal-tasks" && taskCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -8,
                    backgroundColor: "#EF4444",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 3px",
                    lineHeight: 1,
                    zIndex: 1,
                  }}
                >
                  {taskCount}
                </span>
              )}
            </span>
            {!isCollapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>
      </div>
    </div>
  );
};
